// Process trail, drawn as the process — the design view.
//
// The chain used to be an indented tree. A tree is a good picture of what
// contains what and a poor picture of a BUSINESS: nobody drew their process as
// bullet points, and the shape they DID draw was sitting one screen away in the
// Process Flow designer. So the trail is drawn as that diagram, and each step's
// box carries the records of this trail that passed through it — as the
// object's OWN feed card, the same one the tree and the compact list draw, with
// its detail a click away inside the box.
//
// **The layout honours the drawing's SHAPE, not its pixels.** A step's box grows
// with the records inside it, so pinning boxes to the designed x/y would let a
// busy step sit on top of the one below it. Instead the designed positions are
// read as a GRID — columns from the x's people lined up, rows from the y's —
// and the boxes are placed on it. Left-to-right order, what is beside what, and
// which step follows which all survive; only the empty space between them is
// recomputed.
//
// **Boxes are MEASURED, not estimated.** A box's height and the position of
// every record inside it are read back off the DOM after each paint, so a card
// that wrapped to four lines, or a body someone just opened, moves the boxes
// below it instead of sliding under them. The constants here are first-paint
// guesses and nothing else.
//
// **An arrow points at a RECORD, and one source draws ONE arrow.** The chain
// knows which record raised which, so a step holding five plans says which of
// them came from the lead — as a single connector that leaves the source row,
// runs down a spine and branches into each record it raised. Five separate
// arrows off one row were five pictures of one fact. Where a record is behind
// the box's "N more", its branch lands on that row, which says how many it is
// holding.
//
// It pans and zooms through the SAME camera the overview's diagram uses (see
// ./camera), so the two pictures of one process answer the same gestures.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChevronRight, Lock, Maximize2, Minimize2 } from 'lucide-react'

import RowActionsMenu, { MENU_Z_OVER_MODAL, type RowAction } from '../list/RowActionsMenu'
import { CameraControls, useDiagramCamera } from './camera'
import { FeedBodyPanel, FeedRows } from './FeedCard'
import type { FeedBody, FlowNode, FlowSummary, TrailNode } from './contract'
import { nodeKey, recordLinks } from './trailNodes'
import { BOX_W, LANE, buildWires, ghostEdge } from './trailWires'
import type { Ghost, Port, Wire } from './trailWires'

const COL_GAP = 84
const ROW_GAP = 64
const SHOWN = 3
/** How far apart two designed coordinates may be and still count as one
 *  column / row. The designer snaps to a grid; hand-nudged boxes should not
 *  become a column of their own. */
const CLUSTER = 70

// First-paint guesses only — everything below is replaced by a measurement on
// the very next frame. See `measure`.
const EST_HEAD = 36
const EST_ROW = 74
const EST_EMPTY = 34
const EST_MORE = 32

const WIRE = 'var(--color-text-faint, #687281)'
const EDGE = 'var(--color-border-strong, #c7cfd9)'
const ACCENT = 'var(--color-accent, #0061D0)'

/** Every record in the chain, gathered under the step it belongs to, in the
 *  order the walk found them (ancestors first — the spine of the chain). */
function byObject(nodes: TrailNode[], out: Map<string, TrailNode[]>): Map<string, TrailNode[]> {
  for (const n of nodes) {
    const seen = out.get(n.obj_name) || []
    if (n.txn_id || n.restricted) {
      const key = nodeKey(n)
      if (!seen.some((s) => nodeKey(s) === key)) seen.push(n)
    }
    out.set(n.obj_name, seen)
    byObject(n.children || [], out)
  }
  return out
}

/** Designed coordinates → grid indices. Values within `CLUSTER` of each other
 *  are one line of the grid, so two boxes a designer meant to be side by side
 *  stay side by side. */
function lanes(values: number[]): (v: number) => number {
  const sorted = [...new Set(values)].sort((a, b) => a - b)
  const starts: number[] = []
  for (const v of sorted) {
    if (!starts.length || v - starts[starts.length - 1] > CLUSTER) starts.push(v)
  }
  return (v: number) => {
    let idx = 0
    for (let i = 0; i < starts.length; i += 1) if (v >= starts[i] - 1) idx = i
    return idx
  }
}

/** What a box actually measured, once it has been drawn: its height, and where
 *  the middle of each record's header sits inside it. */
interface BoxMetric {
  h: number
  rows: Record<string, number>
  more: number
}

function sameMetrics(a: Record<string, BoxMetric>, b: Record<string, BoxMetric>): boolean {
  const ka = Object.keys(a)
  if (ka.length !== Object.keys(b).length) return false
  for (const k of ka) {
    const x = a[k]
    const y = b[k]
    if (!y || x.h !== y.h || x.more !== y.more) return false
    const rk = Object.keys(x.rows)
    if (rk.length !== Object.keys(y.rows).length) return false
    for (const r of rk) if (x.rows[r] !== y.rows[r]) return false
  }
  return true
}

interface Box {
  node: FlowNode
  records: TrailNode[]
  shown: TrailNode[]
  hidden: number
  restricted: boolean
  x: number
  y: number
  h: number
}

export interface TrailDesignProps {
  flow: FlowSummary | null
  roots: TrailNode[]
  selectedKey?: string
  onSelect: (node: TrailNode) => void
  /** Which card bodies are open, and what has been fetched for them — the SAME
   *  map the tree reads, because it is the same record either way. */
  bodies: Record<string, { open: boolean; busy?: boolean; body?: FeedBody | null }>
  onToggleBody: (node: TrailNode) => void
  /** Open or close a whole set at once — a step's own expand-all. */
  onSetBodies: (nodes: TrailNode[], open: boolean) => void
  /** The kebab for one record — the host's own actions, already rights-gated.
   *  Returning an empty list draws no menu. */
  actionsFor?: (node: TrailNode) => RowAction[]
}

export default function TrailDesign({
  flow, roots, selectedKey, onSelect, bodies, onToggleBody, onSetBodies, actionsFor,
}: TrailDesignProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [metrics, setMetrics] = useState<Record<string, BoxMetric>>({})
  const camera = useDiagramCamera(true)
  const boxEls = useRef<Map<string, HTMLDivElement>>(new Map())

  const records = useMemo(
    () => byObject(roots || [], new Map<string, TrailNode[]>()), [roots])

  const { boxes, ports, width, height } = useMemo(() => {
    const nodes = flow?.nodes || []
    if (!nodes.length) {
      return { boxes: [] as Box[], ports: {} as Record<string, Port>, width: 0, height: 0 }
    }
    const col = lanes(nodes.map((n) => n.x))
    const row = lanes(nodes.map((n) => n.y))

    const sized = nodes.map((n) => {
      const mine = records.get(n.obj_name) || []
      const restricted = mine.some((r) => r.restricted) || n.readable === false
      const open = !!expanded[n.obj_name]
      const shown = restricted ? [] : (open ? mine : mine.slice(0, SHOWN))
      const hidden = restricted ? 0 : mine.length - shown.length
      const measured = metrics[n.id]
      const guess = EST_HEAD
        + (restricted || !mine.length ? EST_EMPTY : 0)
        + shown.length * EST_ROW
        + (hidden > 0 || (open && mine.length > SHOWN) ? EST_MORE : 0)
      return {
        node: n,
        records: mine,
        shown,
        hidden,
        restricted,
        c: col(n.x),
        r: row(n.y),
        h: measured ? measured.h : guess,
      }
    })

    // Row heights are the tallest box in the row, so the rows below it start
    // clear of it however many records landed in this one.
    const rowHeight = new Map<number, number>()
    for (const s of sized) rowHeight.set(s.r, Math.max(rowHeight.get(s.r) || 0, s.h))
    const rowTop = new Map<number, number>()
    let y = 0
    for (const r of [...rowHeight.keys()].sort((a, b) => a - b)) {
      rowTop.set(r, y)
      y += (rowHeight.get(r) || 0) + ROW_GAP
    }

    const out: Box[] = []
    const ports: Record<string, Port> = {}
    for (const s of sized) {
      const bx = s.c * (BOX_W + COL_GAP)
      const by = rowTop.get(s.r) || 0
      out.push({
        node: s.node, records: s.records, shown: s.shown, hidden: s.hidden,
        restricted: s.restricted, x: bx, y: by, h: s.h,
      })
      const m = metrics[s.node.id]
      let acc = EST_HEAD
      for (const r of s.shown) {
        const key = nodeKey(r)
        const inside = m && m.rows[key] != null ? m.rows[key] : acc + EST_ROW / 2
        ports[key] = { step: s.node.id, x: bx, y: by + inside }
        acc += EST_ROW
      }
      // A record behind "N more" is still on the diagram — its arrow lands on
      // the row that says it is hidden.
      const moreInside = m && m.more ? m.more : acc + EST_MORE / 2
      ports[`step:${s.node.id}`] = {
        step: s.node.id,
        x: bx,
        y: by + (s.hidden > 0 ? moreInside : EST_HEAD / 2),
      }
    }
    const width = Math.max(...out.map((b) => b.x + BOX_W), BOX_W) + LANE + 10
    return { boxes: out, ports, width, height: Math.max(y - ROW_GAP, 0) + 8 }
  }, [flow, records, expanded, metrics])

  const stepOfObject = useMemo(() => {
    const m = new Map<string, string>()
    for (const n of flow?.nodes || []) if (!m.has(n.obj_name)) m.set(n.obj_name, n.id)
    return m
  }, [flow])

  // ── measuring ─────────────────────────────────────────────────────────────
  const measure = useCallback(() => {
    const next: Record<string, BoxMetric> = {}
    boxEls.current.forEach((el, id) => {
      const rows: Record<string, number> = {}
      el.querySelectorAll<HTMLElement>('[data-rec]').forEach((r) => {
        const k = r.dataset.rec
        if (k) rows[k] = r.offsetTop + r.offsetHeight / 2
      })
      const more = el.querySelector<HTMLElement>('[data-more-row]')
      next[id] = {
        h: el.offsetHeight,
        rows,
        more: more ? more.offsetTop + more.offsetHeight / 2 : 0,
      }
    })
    setMetrics((prev) => (sameMetrics(prev, next) ? prev : next))
  }, [])

  // After every paint: a card that wrapped, or a body that just arrived, has
  // to move the boxes below it before the arrows are believed.
  useLayoutEffect(() => { measure() })

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(() => measure())
    boxEls.current.forEach((el) => ro.observe(el))
    return () => ro.disconnect()
  }, [measure, boxes.length])

  // ── arrows ────────────────────────────────────────────────────────────────
  const { wires, ghosts } = useMemo((): { wires: Wire[]; ghosts: Ghost[] } => {
    // A record hidden behind its box's "N more" still has a chain: its wire
    // lands on the row that says it is hidden.
    const portOf = (key: string): Port | null => {
      if (ports[key]) return ports[key]
      const obj = key.split(':')[0]
      const step = stepOfObject.get(obj)
      return step ? ports[`step:${step}`] || null : null
    }

    const { wires, joined } = buildWires(
      recordLinks(roots || []), portOf,
      { selectedKey, accent: ACCENT, wire: WIRE },
    )

    const byId = new Map<string, Box>()
    for (const b of boxes) byId.set(b.node.id, b)
    const ghosts: Ghost[] = []
    for (const e of flow?.edges || []) {
      if (joined.has(`${e.from}>${e.to}`)) continue
      const a = byId.get(e.from)
      const b = byId.get(e.to)
      if (!a || !b) continue
      ghosts.push(ghostEdge(
        e.id || `${e.from}-${e.to}`,
        { id: a.node.id, x: a.x, y: a.y, h: a.h },
        { id: b.node.id, x: b.x, y: b.y, h: b.h },
        e.label,
      ))
    }
    return { wires, ghosts }
  }, [roots, ports, stepOfObject, selectedKey, boxes, flow])

  if (!flow || !boxes.length) {
    return (
      <div style={{ padding: 14, fontSize: 14, color: 'var(--color-text-secondary)' }}>
        This object isn’t placed on a process flow yet.
      </div>
    )
  }

  return (
    <div
      // A drawing surface, in the colour the Process Flow designer's canvas
      // uses. Without it the white step boxes sit on white and only the wires
      // between them are visible — the picture reads as lines hanging in space.
      style={{
        position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden',
        background: 'var(--color-bg, #f4f6f9)',
      }}
      tabIndex={0}
      onKeyDown={camera.onKeyDown}
      aria-label="Process trail — drag to pan, arrow keys to move, + and − to zoom"
    >
      <div
        {...camera.bind}
        style={{
          position: 'absolute', inset: 0, padding: 14, touchAction: 'none',
          cursor: camera.grabbing ? 'grabbing' : 'grab',
        }}
      >
        <div style={{
          position: 'relative', width, height,
          transform: `translate(${camera.cam.x}px, ${camera.cam.y}px) scale(${camera.cam.scale})`,
          transformOrigin: 'top left',
        }}>
          <svg width={width} height={height}
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
            {ghosts.map((g) => (
              <g key={g.id}>
                <path d={g.d} fill="none" stroke={EDGE} strokeWidth={1.4} strokeDasharray="4 4" />
                <polygon points={g.head} fill={EDGE} />
              </g>
            ))}
            {wires.map((w) => (
              <g key={w.id}>
                <path d={w.d} fill="none" stroke={w.color} strokeWidth={w.width}
                  strokeLinecap="round" />
                {w.nr ? <circle cx={w.nx} cy={w.ny} r={w.nr} fill={w.color} /> : null}
                {w.head ? <polygon points={w.head} fill={w.color} /> : null}
              </g>
            ))}
          </svg>

          {ghosts.filter((g) => g.label).map((g) => (
            <div key={`l-${g.id}`} style={{
              position: 'absolute', left: g.lx, top: g.ly,
              transform: 'translate(-50%, -50%)', padding: '1px 7px', borderRadius: 999,
              background: 'var(--color-bg-secondary, #fff)', border: '1px solid var(--color-border)',
              color: 'var(--color-text-secondary)', fontSize: 13, whiteSpace: 'nowrap',
            }}>{g.label}</div>
          ))}

          {boxes.map((b) => (
            <StepBox
              key={b.node.id}
              box={b}
              elRef={(el) => {
                if (el) boxEls.current.set(b.node.id, el)
                else boxEls.current.delete(b.node.id)
              }}
              expanded={!!expanded[b.node.obj_name]}
              onToggleMore={() => setExpanded((x) => ({
                ...x, [b.node.obj_name]: !x[b.node.obj_name],
              }))}
              selectedKey={selectedKey}
              onSelect={onSelect}
              bodies={bodies}
              onToggleBody={onToggleBody}
              onSetBodies={onSetBodies}
              actionsFor={actionsFor}
            />
          ))}
        </div>
      </div>
      <CameraControls camera={camera} style={{ position: 'absolute', top: 8, right: 8 }} />
    </div>
  )
}

// ── one step ────────────────────────────────────────────────────────────────

function StepBox({
  box, elRef, expanded, onToggleMore, selectedKey, onSelect,
  bodies, onToggleBody, onSetBodies, actionsFor,
}: {
  box: Box
  elRef: (el: HTMLDivElement | null) => void
  expanded: boolean
  onToggleMore: () => void
  selectedKey?: string
  onSelect: (node: TrailNode) => void
  bodies: Record<string, { open: boolean; busy?: boolean; body?: FeedBody | null }>
  onToggleBody: (node: TrailNode) => void
  onSetBodies: (nodes: TrailNode[], open: boolean) => void
  actionsFor?: (node: TrailNode) => RowAction[]
}) {
  const { node, records, shown, hidden, restricted } = box
  const openable = shown.filter((r) => r.card?.has_body)
  const allOpen = openable.length > 0 && openable.every((r) => bodies[nodeKey(r)]?.open)

  return (
    <div
      ref={elRef}
      style={{
        position: 'absolute', left: box.x, top: box.y, width: BOX_W,
        // Every step is outlined, whether or not it holds the selected record:
        // a box that borrows the selection's colour reads as one more highlight
        // beside the row that already carries it.
        border: `1px ${records.length ? 'solid' : 'dashed'} ${restricted
          ? 'var(--color-warning, #b45309)' : EDGE}`,
        borderRadius: 10,
        // A step that holds records is a card; an empty one is recessed into
        // the canvas. It used to be the other way round, which made the steps
        // with nothing in them the brightest things on the screen.
        background: records.length
          ? 'var(--color-bg-secondary, #fff)' : 'var(--color-surface-2, #f0f3f7)',
        boxShadow: '0 1px 2px var(--color-shadow, rgba(80,90,110,0.06))',
        overflow: 'hidden',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7, padding: '0 10px', height: 36,
        borderBottom: `1px ${records.length ? 'solid' : 'dashed'} var(--color-border)`,
        background: 'var(--color-surface-2, #f0f3f7)',
      }} title={`${node.label} · ${node.obj_name}`}>
        <span style={{
          fontSize: 13.5, fontWeight: 600, flex: 1, minWidth: 0,
          color: records.length ? 'var(--color-text)' : 'var(--color-text-secondary)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{node.label}</span>
        {openable.length > 0 && (
          <button
            type="button"
            onClick={() => onSetBodies(openable, !allOpen)}
            title={allOpen
              ? `Collapse every record in ${node.label}`
              : `Expand every record in ${node.label}`}
            style={{
              flex: 'none', width: 20, height: 20, display: 'grid', placeItems: 'center',
              border: '1px solid var(--color-border)', borderRadius: 4,
              background: 'var(--color-bg-secondary, #fff)', color: 'var(--color-text-secondary)',
              cursor: 'pointer', padding: 0,
            }}
          >
            {allOpen ? <Minimize2 size={11} /> : <Maximize2 size={11} />}
          </button>
        )}
        <span style={{
          flex: 'none', padding: '1px 7px', borderRadius: 999,
          background: 'var(--color-bg-secondary, #fff)',
          border: '1px solid var(--color-border)',
          color: 'var(--color-text-secondary)', fontSize: 13, fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}>{records.length}</span>
      </div>

      {restricted && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 5, padding: '8px 10px',
          fontSize: 13, color: 'var(--color-warning, #b45309)',
        }}>
          <Lock size={12} />
          {records.some((r) => r.exists) ? 'Records exist — no access' : 'You don’t have access'}
        </div>
      )}

      {!restricted && records.length === 0 && (
        <div style={{ padding: '9px 11px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
          Nothing raised here
        </div>
      )}

      {!restricted && shown.map((r) => (
        <RecordRow
          key={nodeKey(r)}
          node={r}
          selected={nodeKey(r) === selectedKey}
          onSelect={onSelect}
          state={bodies[nodeKey(r)]}
          onToggleBody={onToggleBody}
          actions={actionsFor?.(r) || []}
        />
      ))}

      {(hidden > 0 || (expanded && records.length > SHOWN)) && (
        <button
          type="button"
          data-more-row
          onClick={onToggleMore}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, width: '100%', minHeight: 32,
            textAlign: 'left', border: 0, borderTop: '1px solid var(--color-border)',
            background: hidden > 0
              ? 'var(--color-accent-weak, rgba(0,97,208,0.06))' : 'transparent',
            cursor: 'pointer', padding: '7px 11px', fontSize: 13,
            color: hidden > 0 ? ACCENT : 'var(--color-text-secondary)',
          }}
        >
          <ChevronRight
            size={11}
            style={{ transform: hidden > 0 ? undefined : 'rotate(270deg)', flex: 'none' }}
          />
          {hidden > 0
            ? `${hidden} more record${hidden === 1 ? '' : 's'} hidden`
            : 'Show fewer'}
        </button>
      )}
    </div>
  )
}

/** One record inside its step — the object's OWN feed card, its detail a click
 *  away, and the same kebab it carries everywhere else.
 *
 *  The selected row is drawn INSET: a highlight that runs into the box's border
 *  reads as one shape with it, and then neither says which record is selected. */
function RecordRow({ node, selected, onSelect, state, onToggleBody, actions }: {
  node: TrailNode
  selected: boolean
  onSelect: (node: TrailNode) => void
  state?: { open: boolean; busy?: boolean; body?: FeedBody | null }
  onToggleBody: (node: TrailNode) => void
  actions: RowAction[]
}) {
  const canOpen = !!node.card?.has_body
  const open = !!state?.open
  return (
    <div style={{ borderTop: '1px solid var(--color-border)', padding: '4px 5px' }}>
      <div
        data-rec={nodeKey(node)}
        role="button"
        tabIndex={0}
        onClick={() => onSelect(node)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(node) }
        }}
        style={{
          display: 'flex', gap: 8, padding: '5px 5px 6px 6px', borderRadius: 7,
          borderLeft: `3px solid ${selected
            ? ACCENT
            : node.is_anchor ? 'var(--color-accent-weak, rgba(0,97,208,0.35))' : 'transparent'}`,
          background: selected ? 'var(--color-accent-weak, rgba(0,97,208,0.08))' : undefined,
          cursor: 'pointer',
        }}
      >
        {canOpen ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} details for ${node.title}`}
            onClick={(e) => { e.stopPropagation(); onToggleBody(node) }}
            style={{
              flex: 'none', width: 17, height: 17, marginTop: 1, padding: 0,
              display: 'grid', placeItems: 'center', borderRadius: 4, cursor: 'pointer',
              border: `1px solid ${open ? ACCENT : 'var(--color-border)'}`,
              background: open
                ? 'var(--color-accent-weak, rgba(0,97,208,0.10))'
                : 'var(--color-bg-secondary, #fff)',
              color: open ? ACCENT : 'var(--color-text-secondary)',
            }}
          >
            <ChevronRight size={11} style={{ transform: open ? 'rotate(90deg)' : undefined }} />
          </button>
        ) : <span style={{ width: 17, flex: 'none' }} />}

        <div style={{ minWidth: 0, flex: 1 }}>
          {node.card ? <FeedRows rows={node.card.rows || {}} scale={0.92} /> : (
            <div style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>
              {node.title}
            </div>
          )}
          {node.is_anchor && (
            <div style={{ marginTop: 3 }}>
              <span style={{
                padding: '1px 6px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                background: 'var(--color-accent-weak, rgba(0,97,208,0.10))', color: ACCENT,
              }}>you are here</span>
            </div>
          )}
        </div>

        {actions.length > 0 && (
          <div onClick={(e) => e.stopPropagation()} style={{ flex: 'none' }}>
            <RowActionsMenu actions={actions} zIndex={MENU_Z_OVER_MODAL} />
          </div>
        )}
      </div>

      {open && (
        <FeedBodyPanel
          body={state?.body}
          busy={state?.busy}
          style={{ margin: '4px 4px 3px 30px' }}
        />
      )}
    </div>
  )
}
